import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, IconButton, Popover } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ModalFolders from '../pages/modal/ModalFolders';
import ModalUsers from '../pages/modal/ModalUsers';
import ModalCreateUser from '../pages/modal/ModalCreateUser';
import ModalFiles from '../pages/modal/ModalFiles';
import FileSearchBar from '../components/FileSearchBar';
import { createFolder, deleteFolder } from '../api/folderAPI';
import { getAllCompanyUsers, createUser, assignAccessUser, getAccessUser, deleteUser, updateUserStatus, notificationUser} from '../api/userAPI';
import { FolderData } from '../types/types';
import { useTranslation } from 'react-i18next';


const COMPANY_ID = process.env.REACT_APP_COMPANY_ID;

interface User {
  name: string;
  surname: string;
  company: string;
  email: string;
  userId: string;
  language: string;
}

interface Props {
  folders: FolderData[];
  refreshFolders: () => Promise<void>;
  accessType: 'ADMIN' | 'MEMBER';
  search: string;
  setSearch: (value: string) => void;
}

type CompanyUserApi = {
  userId: string;
  userName: string;
  userSurname: string;
  userCompany: string;
  userEmail: string;
  userStatus: string;
  userLanguage: string;
  userAccessList?: { id: string; accessType: string }[];
};

type UserEntry = {
  user: User;
  userStatus: string;
  userAccessType: string;
};

const Menu: React.FC<Props> = ({ folders, refreshFolders, accessType, search, setSearch }) => {

  const { t, i18n } = useTranslation();

  useEffect(() => {
      const storedLang = localStorage.getItem('language') || 'EN';
      i18n.changeLanguage(storedLang);
  }, []);

  // FOLDER MANAGEMENT //
  const [openFolders, setOpenFolders] = useState(false);

  const handleCreateFolder = async (folderNameOrPath: string) => {
    try {
      if (folderNameOrPath.includes('::')) {
        const [parentId, subfolderName] = folderNameOrPath.split('::');
        await createFolder(subfolderName, COMPANY_ID!, parentId);
      } else {
        await createFolder(folderNameOrPath, COMPANY_ID!);
      }

      await refreshFolders();
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleRenameFolder = async (oldName: string, newName: string) => {
    try {
      await refreshFolders();
    } catch (error) {
      console.error('Error refreshing folders after rename:', error);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await deleteFolder(folderId);
      await refreshFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
    }

  };

  // FILE MANAGEMENT //
  const [openFiles, setOpenFiles] = useState(false);

  // USER MANAGEMENT //
  const [openUsers, setOpenUsers] = useState(false);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [users, setUsers] = useState<{ user: User; userStatus: string; userAccessType: string }[]>([]);
  const statusCycle = ['INACTIVE', 'ACTIVE', 'SUSPENDED'] as const;
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifiedUserEmail, setNotifiedUserEmail] = useState<string | null>(null);
  const [notifiedUserName, setNotifiedUserName] = useState<string | null>(null);

  const mapCompanyUsersToEntries = (rawUsers: CompanyUserApi[]): UserEntry[] => {
    return rawUsers.map((u) => {
      const accessType =
        u.userAccessList && u.userAccessList.length > 0
          ? u.userAccessList[0].accessType 
          : 'NOT_ASSIGNED';

      return {
        user: {
          name: u.userName,
          surname: u.userSurname,
          company: u.userCompany,
          email: u.userEmail,
          userId: u.userId,
          language: u.userLanguage,
        },
        userStatus: u.userStatus,
        userAccessType: accessType,
      };
    });
  };

  const handleOpenUsers = async () => {
    try {
      const fetchedRawUsers: CompanyUserApi[] = await getAllCompanyUsers();

      const mappedUsers = mapCompanyUsersToEntries(fetchedRawUsers);

      setUsers(mappedUsers);
      setOpenUsers(true);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };


  const handleCreateUser = async (
    name: string,
    surname: string,
    company: string,
    email: string,
    companyId: string,
    language: string
  ) => {
    try {
      await createUser(name, surname, company, email, companyId, language, false);

      const fetchedRawUsers: CompanyUserApi[] = await getAllCompanyUsers();
      const mappedUsers = mapCompanyUsersToEntries(fetchedRawUsers);

      setUsers(mappedUsers);
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  /////////////////////////////

  const handleNotifyConfirm = async (sendNotification: boolean) => {
    if (sendNotification && notifiedUserEmail && notifiedUserName) {
      await notificationUser(notifiedUserName, notifiedUserEmail);
      console.log(`Notification sent to ${notifiedUserEmail}`);
    }

    setShowNotifyModal(false);
    setNotifiedUserEmail(null);
    setNotifiedUserName(null);
  };

  const getNextStatus = (current: string): string => {
    const index = statusCycle.indexOf(current as typeof statusCycle[number]);
    return statusCycle[(index + 1) % statusCycle.length];
  };

  const handleUserStatus = async (index: number) => {
    const current = users[index];
    const nextStatus = getNextStatus(current.userStatus);

    const updated = [...users];
    updated[index] = {
      ...current,
      userStatus: nextStatus,
    };
    setUsers(updated);
    await updateUserStatus(current.user.email, nextStatus, COMPANY_ID!);
    
    if (nextStatus === 'ACTIVE') {
      setNotifiedUserEmail(current.user.email);
      setNotifiedUserName(current.user.name);
      setShowNotifyModal(true);
    }
  };

  const handlePermissionChange = async (index: number, newAccessType: string) => {
    const userToUpdate = users[index];

    try {
      await assignAccessUser(userToUpdate.user.email, COMPANY_ID!, newAccessType);

      const updated = [...users];
      updated[index].userAccessType = newAccessType;
      setUsers(updated);
    } catch (error) {
      console.error('Error updating user access type:', error);
    }
  };

  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);      
    setConfirmDelete(true);      
  };

  const confirmUserDeletion = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.email, COMPANY_ID!);
      const updatedUsers = users.filter((u) => u.user.email !== userToDelete.email);
      setUsers(updatedUsers);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Failed to delete user: ${userToDelete.email}`);
    } finally {
      setConfirmDelete(false);
      setUserToDelete(null);
    }
  };

  /////////////////////////////////////////////////

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  //const [search, setSearch] = useState('');

  const handleSearchClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleCloseSearch = () => {
    setAnchorEl(null);
  };

  const openSearch = Boolean(anchorEl);

  return (
    <Box sx={{ backgroundColor: '#73AF73', p: 1, borderBottom: '1px solid #ccc' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button sx={{ marginRight: 2, color: 'white' }} variant="text">
           {t('documents')}
        </Button>

        {accessType === 'ADMIN' && (
          <>
            <Button sx={{ marginRight: 2, color: 'white' }} variant="text" onClick={() => setOpenFolders(true)}>
               {t('folders')}
            </Button>
            <Button sx={{ marginRight: 2, color: 'white' }} variant="text" onClick={() => setOpenFiles(true)}>
               {t('files')}
            </Button>
            <Button sx={{ marginRight: 2, color: 'white' }} variant="text" onClick={handleOpenUsers}>
               {t('users')}
            </Button>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={handleSearchClick}>
          <SearchIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>
      {/* Popover for SearchBar */}
      <Popover
        open={openSearch}
        anchorEl={anchorEl}
        onClose={handleCloseSearch}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 400,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
        }}
      >
        <FileSearchBar search={search} setSearch={setSearch} />
      </Popover>

      {/* Modals */}
      <ModalFolders
        open={openFolders}
        onClose={() => setOpenFolders(false)}
        folders={folders}
        onCreate={handleCreateFolder}
        onRename={handleRenameFolder}
        onDelete={handleDeleteFolder}
      />

      <ModalUsers
        open={openUsers}
        onClose={() => setOpenUsers(false)}
        users={users}
        onCreate={() => setOpenCreateUser(true)}
        onPermissionChange={handlePermissionChange}
        onUserStatus={handleUserStatus}
        onDelete={handleDeleteUser}
      />

      <ModalCreateUser
        open={openCreateUser}
        onClose={() => setOpenCreateUser(false)}
        onCreate={handleCreateUser}
        companyId={COMPANY_ID!}
      />

      <ModalFiles open={openFiles} onClose={() => setOpenFiles(false)} />

      {/* Delete dialog */}
      <Dialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setUserToDelete(null);
        }}
      >
        <DialogTitle>{t('confirmDeletion')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
             {t('questionDeleteUser')}<b>{userToDelete?.email}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDelete(false);
              setUserToDelete(null);
            }}
          >
             {t('cancel')}
          </Button>
          <Button onClick={confirmUserDeletion} color="error">
             {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showNotifyModal}
        onClose={() => {
          setShowNotifyModal(false);
          setNotifiedUserEmail(null);
        }}
      >
        <DialogTitle> {t('notifyUser')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
             {t('theUser')} <b>{notifiedUserEmail}</b>  {t('userActivated')}
            <br />
             {t('questionNotifyUser')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleNotifyConfirm(false)}>
             {t('no')}
          </Button>
          <Button onClick={() => handleNotifyConfirm(true)} color="primary">
             {t('yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};


export default Menu;